/* eslint-disable @typescript-eslint/no-explicit-any */
import { Check, ChevronDown } from "lucide-react";
import { Tag as TagIcon } from "@phosphor-icons/react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { usePermission } from "@/hooks/use-premission";
import {
  useAvailableSenderLabels,
  useAddSenderLabels,
  useRemoveSenderLabels,
} from "@/hooks/use-b2c-sender";
import { ISenderProfile } from "@/types/mailbox.types";

/** Normalize sender.labels to array of { _id, name, color } (handles populated or id-only) */
function getLabelList(labels: any): { _id: string; name: string; color: string }[] {
  if (!labels || !Array.isArray(labels)) return [];
  return labels.map((l: any) =>
    typeof l === "string"
      ? { _id: l, name: l, color: "#6b7280" }
      : { _id: l._id, name: l.name ?? l._id, color: l.color ?? "#6b7280" }
  );
}

export function SenderLabelCell({ data }: { data: ISenderProfile }) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: availableLabels = [], isLoading: labelsLoading } = useAvailableSenderLabels();
  const { mutate: addLabels, isPending: isAdding } = useAddSenderLabels();
  const { mutate: removeLabels, isPending: isRemoving } = useRemoveSenderLabels();
  const { hasPermission } = usePermission();

  const labelList = getLabelList((data as any)?.labels);
  const updating = isAdding || isRemoving;

  const renderLabels = () => {
    if (labelList.length === 0) {
      return <span className="text-xs text-muted-foreground">No labels</span>;
    }
    const first = labelList[0];
    return (
      <>
        <Badge
          key={first._id}
          style={{ backgroundColor: first.color }}
          className="text-background text-xs py-0.5 px-1.5 shadow-none rounded-sm"
        >
          {first.name}
        </Badge>
        {labelList.length > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="text-accent-foreground bg-accent text-xs py-0.5 px-1.5 shadow-none rounded-sm">
                +{labelList.length - 1}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-1">
                {labelList.slice(1).map((label) => (
                  <div key={label._id} className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: label.color }}
                    />
                    <span className="text-xs">{label.name}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    );
  };

  if (!hasPermission("mailboxes:edit")) {
    return <div className="h-full flex items-center">{renderLabels()}</div>;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div className="inline-flex w-max min-w-40 justify-between h-8 px-2 rounded-md border border-input hover:bg-accent hover:text-accent-foreground transition-colors">
          <div className="flex items-center gap-1 flex-1 justify-start w-4/5">
            {labelsLoading ? (
              <span className="text-xs text-muted-foreground">Loading...</span>
            ) : (
              renderLabels()
            )}
          </div>
          <ChevronDown className="h-4 ml-1 opacity-50" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {availableLabels.map((label) => {
          const hasLabel = labelList.some((l) => l._id === label._id);
          return (
            <DropdownMenuItem
              key={label._id}
              className="flex items-center gap-2 py-1.5"
              disabled={updating}
              onSelect={(e) => {
                e.preventDefault();
                if (hasLabel) {
                  removeLabels({ id: data._id, labelIds: [label._id] });
                } else {
                  addLabels({ id: data._id, labelIds: [label._id] });
                }
              }}
            >
              <div className="flex items-center gap-2 flex-1">
                <TagIcon weight="fill" size={16} style={{ fill: label.color }} />
                <span className="text-sm">{label.name}</span>
              </div>
              {hasLabel && <Check className="h-3.5 w-3.5" />}
            </DropdownMenuItem>
          );
        })}
        {availableLabels.length === 0 && !labelsLoading && (
          <div className="py-2 px-2 text-xs text-muted-foreground">
            No Sender Profile labels. Create them in Labels.
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
